/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';

export default class GetGraphHistoryService extends BaseService {
  constructor() {
    super(constants.GET_GRAPH_HISTORY, 'graphHistoryStatus');
  }

  getErrorMessage(data, fallback) {
    return data?.error || data?.message || fallback;
  }

  buildHistoryQueryString(limit, offset) {
    const queryParts = [];
    if (limit != null && limit !== '') {
      queryParts.push(`limit=${encodeURIComponent(Number(limit))}`);
    }
    if (offset != null && offset !== '') {
      queryParts.push(`offset=${encodeURIComponent(Number(offset))}`);
    }
    return queryParts.length ? `?${queryParts.join('&')}` : '';
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { graphId, limit, offset } = payload;
    const queryString = this.buildHistoryQueryString(limit, offset);
    const url = `/graphs/${graphId}/history${queryString}`;

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').get(url, { headers: enhancedHeaders });
    }
    return this.getClient('aiadmin').get(url, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    const graphHistoryModel = Framework.getStoreRegistry().getStore('graphHistoryModel');

    if (data && (data.success === true || data.success === undefined)) {
      const records = data.data || [];
      const meta = {
        total: data.total,
        hasMore: data.hasMore,
        offset: data.offset,
        limit: data.limit,
        count: data.count
      };
      graphHistoryModel.setHistoryData(records, meta);
    } else {
      const msg = this.getErrorMessage(data, 'Failed to fetch graph history');
      graphHistoryModel.setError(msg);
    }
  }

  handleError(error) {
    const graphHistoryModel = Framework.getStoreRegistry().getStore('graphHistoryModel');
    const { response } = error;

    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
      const msg = this.getErrorMessage(data, 'Failed to fetch graph history');
      graphHistoryModel.setError(msg);
    } else if (error.message === 'timeout of 60000ms exceeded') {
      graphHistoryModel.setError('Request timeout. Please try again.');
    } else {
      const msg = error.message || 'Failed to fetch graph history';
      graphHistoryModel.setError(msg);
    }
  }
}
